import type { Drive } from '@qse/constants'
import { UserRoleEnum } from '@qse/constants'
import { useCallback, useMemo } from 'react'

import { useUserStore } from './user'

/**
 * Hook for managing quota and storage information
 */
export const useQuotaManagement = () => {
  const { user, updateDriveQuota, setUploadStatus, setDeletingStatus } = useUserStore()

  // Get current quota data
  const quotaData = {
    storageUsed: user?.drive?.quotaSummary?.storage?.used || 0,
    storageQuota: user?.drive?.quotaSummary?.storage?.quota || 0,
    serviceUsed: user?.drive?.quotaSummary?.service?.used || 0,
    serviceQuota: user?.drive?.quotaSummary?.service?.quota || 0,
  }

  // Update drive quota
  const updateQuota = useCallback(
    (newDrive: Drive) => {
      updateDriveQuota(newDrive)
    },
    [updateDriveQuota],
  )

  // Upload status handlers
  const handleUploadStart = useCallback(() => {
    setUploadStatus(true)
  }, [setUploadStatus])

  const handleUploadEnd = useCallback(() => {
    setUploadStatus(false)
  }, [setUploadStatus])

  // Deletion status handlers
  const handleDeletionStart = useCallback(() => {
    setDeletingStatus(true)
  }, [setDeletingStatus])

  const handleDeletionEnd = useCallback(() => {
    setDeletingStatus(false)
  }, [setDeletingStatus])

  return {
    // Current quota data
    quotaData,

    // Update methods
    updateQuota,

    // Status management
    handleUploadStart,
    handleUploadEnd,
    handleDeletionStart,
    handleDeletionEnd,

    // Drive status
    driveStatus: user?.drive?.status || 'READY',
    isDriveReady: user?.drive?.status === 'READY',
  }
}

export default useQuotaManagement

/**
 * Selector: returns true when storage.used > storage.quota for the current user.
 * SUPER_ADMIN is never over-quota.
 */
export const useIsOverQuota = (): boolean => {
  const user = useUserStore(s => s.user)

  return useMemo(() => {
    if (!user || user.userRole === UserRoleEnum.SUPER_ADMIN) return false

    const storage = user.drive?.quotaSummary?.storage

    if (!storage) return false

    return storage.used >= storage.quota
  }, [user])
}
